import { Link, Navigate, useParams } from 'react-router-dom';
import { UserX } from 'lucide-react';
import { Button } from '@clarix/ui';
import { EmptyState } from '@clarix/ui-shared';
import { useAuthStore } from '@clarix/auth-store';
import { useDelayedLoading } from '@clarix/hooks';
import { useDirectoryMember } from '@clarix/services-users';
import { IdentityCard } from './IdentityCard';
import { ProfileShell, ProfileSkeleton } from './ProfileSection';
import { ReportsToCard } from './ReportsToCard';
import { roleLine } from './roleLine';
import { WorkDetailsCard } from './WorkDetailsCard';

/**
 * Read-only profile of a colleague, reached from a Reports to row. Shows the
 * directory facts only — account, security and access stay on the owner's own page.
 */
export function ColleagueProfilePage() {
  const { membershipId = null } = useParams<{ membershipId: string }>();
  const myUserId = useAuthStore((s) => s.user?.sub ?? null);
  const colleague = useDirectoryMember(membershipId);
  const showSkeleton = useDelayedLoading(colleague.isLoading);
  const member = colleague.data;

  // Own row: the full editable profile lives at /profile.
  if (member && myUserId != null && member.user_id === myUserId) {
    return <Navigate to="/profile" replace />;
  }

  if (showSkeleton) return <ProfileSkeleton columns={1} />;

  if (!member) {
    return (
      <EmptyState
        icon={UserX}
        title="Profile not found"
        description="This person is not in your workspace directory."
        action={
          <Button asChild variant="outline" size="sm">
            <Link to="/profile">My profile</Link>
          </Button>
        }
        fill
      />
    );
  }

  return (
    <ProfileShell
      aside={
        <>
          <IdentityCard
            name={member.name}
            email={member.email}
            emailAsLink
            subtitle={roleLine(member.designation?.name, member.department?.name)}
          />
          <ReportsToCard member={member} />
        </>
      }
      columns={[<WorkDetailsCard key="work" member={member} />]}
    />
  );
}
